import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DTypeFormAutocompleteField } from '@grupo-constant/next-dtype/form';
import { AutocompleteOption, DTypeViewableCol } from '@grupo-constant/next-dtype/types';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { LocalizePipe } from '../../../pipes/localize.pipe';

@Component({
    selector: 'autocomplete-field-component',
    templateUrl: './autocomplete-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeAutocompleteFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy {

    @Input() field: DTypeFormAutocompleteField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    displayedValue: string;
    filteredOptions: Observable<AutocompleteOption[]>;

    private _unsubscribeAll = new Subject<void>();

    constructor(private localize: LocalizePipe) { }

    ngOnInit() {
        this.displayedValue = this.getDisplayValue(this.field.control.value);
        this.field.control.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged()
            )
            .subscribe(value => this.displayedValue = this.getDisplayValue(value?.code));

        this.filteredOptions = this.field.control.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                startWith(this.field.control.value ?? ''),
                map(value => this.filter(value ?? ''))
            );

        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    displayValue = code => this.getDisplayValue(code);

    getDisplayValue(code: string) {
        let displayValue: string;
        if (code === undefined) {
            return '';
        }
        if (typeof code === 'string' && code.length > 0) {
            displayValue = this.getOptionLabel(code);
        }
        return displayValue ?? code;
    }

    private getOptionLabel(code: string) {
        if (!code) {
            return null;
        }
        const option = (this.field.options ?? []).find(o => o.code === code);
        return option ? this.localize.transform(option.value) : null;
    }

    private filter(value: string) {
        const filterValue = value.toLowerCase();
        return (this.field.options ?? []).filter(option => this.localize.transform(option.value).toLowerCase().includes(filterValue) || option.code.toLowerCase() === filterValue);
    }

}
